// §concierge-oto — the post-payment ONE-TIME OFFER (OTO) funnel page.
//
// After a patient pays for a Beluga treatment visit and the visit is created,
// New York patients see one page offering Dr. Mavroson's three concierge
// programs as ONE-CLICK add-ons: the card they just used is on file, so each
// button charges it and enrols them without a second intake. Dr. Mavroson's
// team and Beluga both work from the health profile the patient already
// completed; Beluga keeps fulfilling and dispensing, Dr. Mavroson's dashboard
// is alerted with the patient's info.
//
// Owner request 2026-10-11. Backend contract: docs/BACKEND_CONCIERGE_OTO_PRD.md.
// Dashboard: docs/DASHBOARD_CONCIERGE_OTO_PRD.md.
//
// DARK until the backend ships: REACT_APP_CONCIERGE_OTO_ENABLED=1 turns on
// (a) card-on-file at the treatment checkout for eligible patients and (b) the
// offer page. Both are no-ops without it.
import { CONCIERGE_PROGRAMS } from './conciergePrograms';
import { PRACTICE, PRACTITIONER_IMAGE, isStateServed } from './conciergeCommon';

export const isConciergeOtoEnabled = () => process.env.REACT_APP_CONCIERGE_OTO_ENABLED === '1';

export const CONCIERGE_OTO_PRICE_LIST_DATE = '2026-10-11';

// The three programs in the order the owner named them. Prices come from the
// same constants the standalone intakes use, so the OTO can never drift from
// /advanced-peptide, /advanced-weight-loss and /advanced-longevity.
export const OTO_PROGRAM_KEYS = ['advancedPeptide', 'advancedWeightLoss', 'advancedLongevity'];

// Short, benefit-first copy for the offer cards (the standalone intakes carry
// the long form). Everything priced is read from CONCIERGE_PROGRAMS at render.
const CARD_COPY = {
  advancedPeptide: {
    eyebrow: 'Most popular with treatment patients',
    tagline: 'Physician-guided peptide therapy for recovery, metabolism and healthy aging — with direct access to Dr. Mavroson.',
    highlights: [
      'Direct cell-phone access to Dr. Mavroson',
      'Personalized peptide protocol reviewed with your current treatment',
      'Member pricing on advanced labs and testing',
    ],
    image: '/programs/concierge-advanced-peptide.jpg',
  },
  advancedWeightLoss: {
    eyebrow: 'Pairs with GLP-1 treatment',
    tagline: 'Concierge weight-management oversight on top of your medication: planning, follow-up and a doctor you can text.',
    highlights: [
      'Direct cell-phone access to Dr. Mavroson',
      'Ongoing clinical follow-up on your weight-loss plan',
      'Member pricing on hormone, GI-map and epigenetic-age testing',
    ],
    image: '/programs/concierge-weight-management.jpg',
  },
  advancedLongevity: {
    eyebrow: 'Functional medicine',
    tagline: 'A root-cause look at energy, hormones and healthy aging, with functional-medicine labs and a plan built around your results.',
    highlights: [
      'Comprehensive functional-medicine lab evaluation',
      'Member pricing on DUTCH hormone, GI-map and microplastics testing',
      'Direct cell-phone access to Dr. Mavroson',
    ],
    image: '/programs/concierge-longevity.jpg',
  },
};

// Normalise GET /concierge/oto/offer (object keyed by program, or an array
// with `program`) → { [key]: { todayCents, monthlyCents, consentText, consentSha256 } }.
export function normalizeOffer(raw) {
  if (!raw) return null;
  const src = raw.programs || raw.offer || raw;
  const out = {};
  if (Array.isArray(src)) {
    src.forEach((o) => { if (o && (o.program || o.key)) out[o.program || o.key] = o; });
  } else if (typeof src === 'object') {
    Object.keys(src).forEach((k) => { if (src[k] && typeof src[k] === 'object') out[k] = src[k]; });
  }
  return Object.keys(out).length ? out : null;
}

// `offer` (from the server) prices the buttons and supplies the consent text;
// `hide` lists programs the patient already holds (payment response
// `alreadyEnrolled`). Without an offer (preview only) the constants price it.
export function otoPrograms({ offer = null, hide = [] } = {}) {
  const hidden = new Set(hide || []);
  return OTO_PROGRAM_KEYS.filter((key) => !hidden.has(key)).filter((key) => !offer || offer[key]).map((key) => {
    const p = CONCIERGE_PROGRAMS[key];
    const c = CARD_COPY[key];
    const o = (offer && offer[key]) || null;
    const todayCents = o && Number.isFinite(Number(o.todayCents)) ? Number(o.todayCents) : p.billing.todayCents;
    const monthlyCents = o && Number.isFinite(Number(o.monthlyCents)) ? Number(o.monthlyCents) : p.billing.monthlyCents;
    const priceLabel = todayCents === monthlyCents
      ? `$${todayCents / 100}/mo`
      : `$${todayCents / 100} first month, then $${monthlyCents / 100}/mo`;
    return {
      key,
      name: p.name,
      planId: p.planSlug,
      program: key,
      billing: p.billing,
      // "Add for $299/mo" / "Add for $199 first month, then $299/mo"
      buttonLabel: `Add for ${priceLabel}`,
      priceLabel,
      todayCents,
      monthlyCents,
      consentText: (o && o.consentText) || '',
      consentSha256: (o && o.consentSha256) || null,
      eyebrow: c.eyebrow,
      tagline: c.tagline,
      highlights: c.highlights,
      image: c.image,
      footnote: p.footnote,
    };
  });
}

// Which flows get the offer: Beluga treatment intakes only — never the
// concierge intakes themselves, never follow-ups, never lab-only visits.
export const OTO_EXCLUDED_VISIT_TYPES = new Set([
  'advancedPeptide', 'advancedWeightLoss', 'advancedLongevity',
  'labAnalysis', 'labTestingAutoAuthorization',
]);

// State from the shipping address (the treatment flows all collect one),
// falling back to a residence answer.
export function patientState(answers = {}) {
  const addr = answers['shipping-address'] || answers.shippingAddress || answers.address || {};
  return String(addr.state || answers['state-of-residence'] || answers.state || '').toUpperCase();
}

// Should the treatment checkout store the card (verifyBuyer → the backend
// CreateCard)? Yes whenever the offer could follow: flag on + served state.
// Decided BEFORE payment, because the one-click add-on needs the card on file.
export function otoCardOnFile(answers = {}, visitType = '') {
  if (!isConciergeOtoEnabled()) return false;
  if (OTO_EXCLUDED_VISIT_TYPES.has(visitType)) return false;
  return isStateServed(patientState(answers));
}

// Should the offer page render after the visit is created? Same gate, plus
// the visit exists and the backend confirmed the card is on file (it says so
// on the payment response as `cardOnFile: true`; without it there is nothing
// to charge with one click, so the page is skipped rather than shown broken).
export function otoAvailable({ answers = {}, visitType = '', visitResult = null, paymentResult = null } = {}) {
  if (!otoCardOnFile(answers, visitType)) return false;
  if (!visitResult || !visitResult.masterId) return false;
  const cardOnFile = (paymentResult && paymentResult.cardOnFile === true) || visitResult.cardOnFile === true;
  return cardOnFile;
}

// POST /concierge/oto/enroll body for one program (BE PRD §2).
export function otoEnrollPayload({ program, visitResult, paymentResult, visitType, answers = {}, offerProgram = null }) {
  const p = CONCIERGE_PROGRAMS[program];
  const contact = answers['basic-contact'] || {};
  return {
    program,
    planId: p.planSlug,
    masterId: visitResult?.masterId,
    paymentId: paymentResult?.paymentId || visitResult?.paymentId || null,
    visitType,
    email: contact.email || undefined,
    // What the button showed — from the server's offer when we have it.
    expectedTodayCents: offerProgram ? offerProgram.todayCents : p.billing.todayCents,
    expectedMonthlyCents: offerProgram ? offerProgram.monthlyCents : p.billing.monthlyCents,
    ...(offerProgram && offerProgram.consentSha256 ? { consentSha256: offerProgram.consentSha256 } : {}),
    priceListDate: CONCIERGE_OTO_PRICE_LIST_DATE,
    source: 'post_checkout_oto',
  };
}

// Programs the patient already holds (payment response `alreadyEnrolled`).
export function alreadyEnrolledFrom(paymentResult, visitResult) {
  const a = (paymentResult && paymentResult.alreadyEnrolled) || (visitResult && visitResult.alreadyEnrolled) || [];
  return Array.isArray(a) ? a : [];
}

export const OTO_PRACTICE = { ...PRACTICE, image: PRACTITIONER_IMAGE };

// Honest OTO framing: the page is shown once, right after checkout. The
// programs themselves stay available at their standalone intakes, so we never
// claim the price disappears — only that the one-click, no-new-forms add-on is
// a this-page-only convenience.
export const OTO_COPY = {
  eyebrow: 'Your order is complete',
  headline: 'One more thing before you go, New York.',
  subhead: "You can add Dr. Mavroson's concierge care to your treatment with one click — no new forms. We'll use the health profile you just completed, and your card on file.",
  oneTime: 'This one-click add-on is only offered on this page. You can always enrol later through the regular program sign-up.',
  howItWorks: [
    'Your prescriptions, if any, are still fulfilled and dispensed through Beluga Health, exactly as your treatment today.',
    "Dr. Mavroson's team is alerted with your information and reaches out within one business day.",
    'Billed to the card you just used. Cancel anytime: full refund within 2 days, then 30 days\' notice.',
  ],
  decline: 'No thanks, take me to my confirmation',
  continueAfterAdd: 'Continue to my confirmation',
  nyOnly: 'Available to New York patients',
};
